'use client'

import ReviewsHero from '@/components/content/reviews/root/ReviewsHero'
import ReviewThumbnail from '@/components/content/reviews/root/ReviewThumbnail'

import { FeaturedPlacement } from '@/lib/analytics'
import { PageContextProvider } from '@/components/content/common/PageContext'
import FilterableContainer from '@/components/content/common/FilterableContainer'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'

import type { FeaturedSection } from '@/types/app'
import type { App, Category, AppCategoryRecommendation, Review } from '@/payload-types'

function filterAndSortReviews(apps: App[], selectedCategoryId: number | null) {
  const recommendationOrder: Record<Recommendation, number> = {
    [Recommendation.TopPick]: 1,
    [Recommendation.Recommended]: 2,
    [Recommendation.Capable]: 3,
    [Recommendation.NotRecommended]: 4,
  }

  if (selectedCategoryId === null) {
    return apps.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  }

  const filteredApps = apps.filter((app) =>
    app.categoryRecommendations?.docs?.some(
      (doc): doc is AppCategoryRecommendation =>
        typeof doc !== 'number' && (doc.category as Category).id === selectedCategoryId,
    ),
  )

  return filteredApps.sort((a, b) => {
    const aDocs = a.categoryRecommendations?.docs || []
    const bDocs = b.categoryRecommendations?.docs || []

    const aRec = aDocs.find(
      (doc): doc is AppCategoryRecommendation =>
        typeof doc !== 'number' && (doc.category as Category).id === selectedCategoryId,
    )
    const bRec = bDocs.find(
      (doc): doc is AppCategoryRecommendation =>
        typeof doc !== 'number' && (doc.category as Category).id === selectedCategoryId,
    )

    if (aRec && bRec) {
      const aPriority = recommendationOrder[aRec.recommendation] ?? Infinity
      const bPriority = recommendationOrder[bRec.recommendation] ?? Infinity
      return aPriority - bPriority
    }

    if (aRec) return -1 // `a` has a recommendation, `b` doesn't
    if (bRec) return 1 // `b` has a recommendation, `a` doesn't

    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })
}

type ReviewsContainerProps = {
  apps: App[]
  featuredReviews: Review[]
  reviewsFeaturedTagSections: FeaturedSection[]
  categories: Category[]
}

export default function ReviewsContainer({
  apps,
  featuredReviews,
  reviewsFeaturedTagSections,
  categories,
}: ReviewsContainerProps) {
  return (
    <PageContextProvider scrollOffset={145}>
      <ReviewsHero reviews={featuredReviews} />
      <FilterableContainer
        collection="reviews"
        categories={categories}
        items={apps}
        featuredItems={reviewsFeaturedTagSections}
        element={({ item, type }: { item: App; type?: string }) => (
          <ReviewThumbnail reviewApp={item} type={type} placement={FeaturedPlacement.REVIEW_FEED} />
        )}
        filterItems={(items, filter) => filterAndSortReviews(items, filter.id)}
        cols={2}
      />
    </PageContextProvider>
  )
}
